/* eslint-disable import/no-internal-modules */
import type { Coordinate } from '@studio/js-utils/geometry';
import type { Account } from '@studio/models/account';
import type { Agent } from '@studio/models/agent';
import type { AppId } from '@studio/models/app';
import type { Device } from '@studio/models/device';
import type { Organization } from '@studio/models/organization';
import type { RoomRecord } from '@studio/models/room';
import type { User } from '@studio/models/user';
import type * as Store from '@studio/react-utils/store';
import type { T_Maybe } from '@tryiris-ai/js-types';
import type { AutoTrackingReservationStatePair } from '#~/auto-tracking/provider/types.js';
import type { UserProvider } from '#~/auth/user-provider.js';
import type { FastFrameSelectStage } from '#~/control/components/interaction/fast-frame/types.js';
import type { Tool } from '#~/control/index.js';
import type { Registry } from '#~/device/registry/types.js';
import type {
  MultiAddSkipReason,
  Notification,
  NotificationStatus,
  Options
} from '#~/notifications/types.js';
import type { Room } from '#~/room/types.js';
import type { AutoLinkState } from '#~/routes/verify-auto-link/v2/auto-link-types.js';
import type {
  AgentRegistryCreateInviteResponse,
  AgentRegistryMembership
} from '@tryiris-ai/common.protojs/api/v1/agent_registry';

/**
 * Multi-Add origin record stored on the depot's `multiAddOriginByDeviceId`
 * slice. Each successful `runBulkCommit` device adds one entry keyed
 * by `deviceId`.
 *
 * Tracks both the parent `batchId` (so the observer hook can group
 * multiple devices from the same multi-add operation) and `createdAt`
 * for ordering / future expiry policies. Session-scoped — see the
 * `multiAddOriginByDeviceId` field comment on `State` below.
 *
 * Per `think-in-systems` (ai.knowledge/global): minimal shape — only the
 * fields the consumer hook actually needs.
 */
export interface MultiAddOrigin {
  /** Parent batch id from `runBulkCommit`. */
  batchId: string;
  /** Wall-clock at registration. */
  createdAt: Date;
}

/** Mutable data depot draft. */
export type Draft = Store.Draft<State>;

/** Data depot producer. */
export type Producer = Store.Producer<State>;

/** Data depot state. */
export type State = Readonly<{
  /** Account data. */
  account?: T_Maybe<Account>;

  /** Agent data. */
  agents: Record<Agent['id'], Agent>;

  /** Unique application identifier. */
  appId: AppId;

  /** Auto tracking data. */
  autoTrackingInstances: Record<
    Agent['id'],
    Record<Device['id'], AutoTrackingReservationStatePair>
  >;

  /** Network state. */
  network: {
    /** NATS connection. */
    isConnected: boolean;

    /* Online status. */
    isOnline: boolean;
  };

  /** Notifications. */
  notifications: Record<Notification['id'], [Notification, T_Maybe<Options>, NotificationStatus]>;

  /**
   * Auto-configured fields — Multi-Add Phase 2.
   *
   * Tracks which form fields were populated by Extended Discovery (template
   * fingerprint match) or the ONVIF probe rather than typed by the user.
   * The settings panel's `<AutoConfiguredLabel>` subscribes to this slice
   * to render a small "Auto Configured" tag beside each auto-filled input.
   * When the user edits the value the entry evicts and the label hides.
   *
   * Shape: keyed by `Device['id']` then by `fieldPath` (e.g. `"manufacturer.brand"`).
   * The recorded `value` is the original auto-filled value — the label
   * component compares the current input value against it to detect user
   * edits and self-evict.
   *
   * Scope: session-only (in-memory; cross-session persistence is
   * intentionally deferred to a later phase per the session-only scope decision).
   * Lives on the depot (singleton store) rather than any per-modal store
   * because the add-flow writes the entries but the settings panel reads
   * them after the device commits — the two stores have independent
   * lifetimes.
   *
   * Per `think-in-systems` (ai.knowledge/global) — the slice is a
   * parallel primitive; mutations live in
   * `device/store/auto-configured-fields.slice.ts`.
   */
  autoConfiguredFields: Record<Device['id'], Record<string, string>>;

  /**
   * Multi-Add origin provenance — post-add "needs configuration"
   * notifications.
   *
   * Tracks which devices were committed via the multi-add bulk pipeline
   * (`runBulkCommit`) so the observer hook can fire post-add toast
   * notifications ONLY for multi-added devices. Wizard / single-add
   * devices never appear here and therefore never fire post-add
   * notifications. The boundary is load-bearing.
   *
   * Session-scoped: in-memory only, NOT persisted across app restart
   * and NOT carried over state-sync. Resets to `{}` when the depot is
   * recreated. Per `think-in-systems` (ai.knowledge/global): client-only
   * primitive lives client-only — no protobuf, no URX core.
   *
   * Per `reference-rules-in-comments` (ai.knowledge/global).
   */
  multiAddOriginByDeviceId: Record<string, MultiAddOrigin>;

  /**
   * Per-device LAST-POSTED desired-alert identity for the post-add
   * "needs configuration" observer (ENG-4486 declarative reconciliation).
   * Keyed by `deviceId`; the value is the toast id of the alert currently
   * considered live for that device — `bucketToastId(batchId, deviceId,
   * bucket)` for a specific alert, or the `:combined:` id for the combined
   * alert. A device with NO alert has NO entry.
   *
   * The observer computes each device's DESIRED alert as a pure function of
   * its current missing set (0 → none, 1 → specific, ≥2 → combined) and
   * reconciles against this slice: it re-posts ONLY when the desired
   * identity differs from what is stored. This is what makes "exactly one
   * alert per device" true by construction, auto-clears an alert when its
   * setting resolves (identity → none), mutates combined↔specific on
   * partial resolution, and RESPECTS user dismissal (a dismissed toast is
   * never re-posted while the desired identity is unchanged). Replaces the
   * old per-(device, bucket) sticky dedup + imperative supersede.
   *
   * Session-scoped (resets with the depot). Per
   * `reference-rules-in-comments` (ai.knowledge/global).
   */
  lastDesiredAlertByDevice: Record<string, string>;

  /**
   * Per-device HELD connection-failure buckets ('video-failed' /
   * 'control-failed').
   *
   * Why a hold exists: an unreachable camera does not sit still. Past its
   * grace window the encoder alternates CONNECTION_FAILED <-> CONNECTING on a
   * ~5s cadence — INTENTIONALLY, because that cycle is what animates the
   * connection badge in the centre of the preview while the device keeps
   * retrying. But the needs-attention chip and the "Configure Video"
   * notification are TERMINAL verdicts, not progress indicators: following the
   * cycle made both flicker on and off every few seconds. This slice holds the
   * verdict steady for those two surfaces without flattening the cycle the
   * badge depends on.
   *
   * Why the depot and not a hook-local ref: both consumers remount routinely
   * (tile selection, arrangement change, left-tray tab switch, and the
   * remove+re-add a settings commit performs), and they are two independent
   * hooks with no shared parent — so hook-local state would be lost and the
   * two surfaces would disagree. Depot scope is what makes them agree.
   *
   * Note on reactivity: what re-renders `useChipState` is its
   * `room.devices[id]` subscription, and that Device record DOES carry live
   * per-aspect status (`protobuf/device.ts`) — that is what lets the writer
   * observe a failure and its release. This slice is shared state, not a
   * reactivity mechanism; do not remove that subscription on the assumption
   * the slice covers it.
   *
   * Session-scoped (resets with the depot). Per
   * `reference-rules-in-comments` (ai.knowledge/global).
   */
  connectionFailureHoldByDevice: Record<string, MultiAddSkipReason[]>;

  /**
   * Per-(device, sub-tab) dismissal map for the Advanced Settings
   * "needs attention" warning icons (Multi-Add Phase 2).
   *
   * Each device tracks which of the four sub-tabs (`authentication` /
   * `video` / `control` / `driver`) the user has dismissed in the
   * current session. A dismissed tab does NOT display the warning
   * icon, regardless of the underlying status — see
   * `deriveNeedAttentionTabs` in `video/components/settings/
   * iris-connection/need-attention-tabs.ts` for the suppression logic.
   *
   * Shape: `dismissedNeedAttentionTabs[deviceId]?.[tab] === true` when
   * the user has dismissed that tab; missing / undefined means "not
   * dismissed". Per-tab not per-device — dismissing auth still leaves
   * video flagged.
   *
   * Session-scoped (resets with the depot). Per
   * `multi-writer-registry-completeness`
   * (ai.knowledge/platform/studio.client): the depot initial-state
   * site populates this to `{}` so consumers can index without a
   * `?? {}` guard.
   *
   * Per `reference-rules-in-comments` (ai.knowledge/global).
   */
  dismissedNeedAttentionTabs: Record<
    Device['id'],
    Partial<Record<'authentication' | 'video' | 'control' | 'driver', true>>
  >;

  /**
   * Per-(device, sub-tab) analytics-emit dedup set (Multi-Add Phase 2).
   * Records which `MULTI_ADD_SUBTAB_NEEDS_ATTENTION_SHOWN`
   * events have already been emitted for a given `(deviceId, tab)`
   * pair so the `useNeedAttentionTabs` hook fires the event exactly once
   * per `false → true` transition.
   *
   * Key encoding: `<deviceId>:<tab>`.
   * Value is always `true` once recorded.
   *
   * Session-scoped (resets with the depot). Separate slice from
   * `dismissedNeedAttentionTabs` because the two concerns have
   * different lifetimes: dismissal is user-initiated (manual click),
   * analytics dedup is render-initiated (one emit per session per
   * transition).
   *
   * Per `reference-rules-in-comments` (ai.knowledge/global).
   */
  shownSubtabAnalytics: Record<string, true>;

  /** Organization Data. */
  organizations: Record<Organization['id'], Organization>;

  /** Synchronizer status flags. */
  synchronizer: {
    /** Whether an initial registry sync has completed. */
    hasRegistryLoaded: boolean;

    /** Whether the registry is currently loading. */
    isRegistryLoading: boolean;
  };

  /** Immer producer function. */
  produce: Producer;

  /** Device registry. */
  registry: Registry;

  /** Room Records. */
  roomRecords: Record<Room['id'], RoomRecord>;

  roomInviteRecords: AgentRegistryCreateInviteResponse[];

  roomMembershipRecords: AgentRegistryMembership[];

  /** Room data. */
  rooms: Record<Room['id'], Room>;
  /** Place to store tool state that is not persisted or synced. */
  toolStates: {
    /** Fast frame initial scale. */
    fastFrameInitialScale: number;

    /** Fast frame on exit tool. */
    fastFrameOnExitTool: T_Maybe<Tool>;

    /** Fast frame select stage. */
    fastFrameSelectStage: FastFrameSelectStage;

    /** Fast frame initial position. */
    toolInitialPosition: T_Maybe<Coordinate>;
  };

  /**
   * Application user.
   *
   * ENG-3451: `provider` is the registry's persisted Auth0 identity provider
   * (see auth/user-provider.ts). Optional because it is present only when the
   * registry read succeeded AND the server sent a recognized value — the
   * Auth0-only fallback user and unknown/empty wire values leave it absent,
   * which consumers must treat as "unknown, preserve existing behavior".
   */
  user: T_Maybe<User & { provider?: UserProvider }>;

  /**
   * Boot lifecycle slice. Single source of truth for the unified
   * BootOverlay — replaces the cascade of per-component loading
   * screens (`AutoLinkLoadingScreen` × 3 sites) that used to remount
   * and flicker between Guard → Root → Placeholder.
   *
   * Per `stay-in-scope` (ai.knowledge/global): overlay visibility plus
   * committed observation. Registry-loaded already lives on
   * `synchronizer.hasRegistryLoaded` and is consumed directly by the
   * overlay; do not duplicate it here.
   */
  boot: {
    /** Mirror of the AutoLinkController's current state. */
    autoLinkState: AutoLinkState;

    /** Mirror of `useRootBootstrap().isReady`. */
    isRootReady: boolean;

    /** Actual overlay ownership, including its first-dismissal latch. */
    isOverlayVisible: boolean;

    /** Boot-blocking error. When non-null, BootOverlay shows error UI with retry. */
    error: BootError | null;

    /**
     * Committed-readiness facts. Overlay owns `ready`/`failed`/`awaiting_user`;
     * the room publisher fills access and the room-facts marker without
     * claiming overlay success.
     */
    observation: BootAttemptObservation;
  };
}>;

/**
 * Whether the room publisher has actually observed this room's facts yet.
 * Readiness only ever asked "not unknown" of the previous enumeration and
 * inventory classes -- an empty Studio and a populated one both qualify -- so
 * the empty/populated split described nothing any consumer acted on.
 */
export type BootRoomFacts = 'unknown' | 'observed';

export type BootAccess = 'unknown' | 'viewer' | 'writer';

export type BootAttemptObservation = {
  outcome: 'pending' | 'awaiting_user' | 'ready' | 'failed';
  roomId: string | null;
  access: BootAccess;
  registry: 'unknown' | 'loaded' | 'error';
  roomFacts: BootRoomFacts;
  accountId: string | null;
  bootId: string | null;
};

export const INITIAL_BOOT_OBSERVATION: BootAttemptObservation = {
  outcome: 'pending',
  roomId: null,
  access: 'unknown',
  registry: 'unknown',
  roomFacts: 'unknown',
  accountId: null,
  bootId: null
};

export type BootError = {
  message: string;
  detail?: string;
  severity: 'fatal' | 'retryable';
  retryFn: (() => void) | null;
  /**
   * Optional account escape. Set when the error is account-scoped (e.g. the
   * ENG-4166 cross-account bridge link: retry cannot help a signed-in account
   * that does not own the linked studio) so the screen offers "Sign out"
   * instead of trapping the user behind retry-forever.
   */
  signOutFn?: (() => void) | null;
};
